import React, { createContext, useContext, useState, useEffect } from 'react';
import useAuth from '@jetshop/core/components/AuthContext/useAuth';
import { useQuery } from 'react-apollo';
import customerIdQuery from '../components/Gamifiera/CustomerIdQuery.gql';
import storeLocatorQuery from '../components/StoreLocator/StoreLocator.gql';
import ChannelContext from '@jetshop/core/components/ChannelContext';

export const FAVORITE_STORE_KEY = 'favorite_store';
export const WAREHOUSE_STOCK_LEVEL_THRESHOLD = 0;

// Channel to region mapping (same as StoreStock.js)
const countries = {
  'B2C-sv': 'Sverige',
  SV: 'Sverige',
  'B2C-dk': 'Danmark',
  DK: 'Danmark',
  'B2C-no': 'Norge',
  NO: 'Norge',
  'B2C-fi': 'Suomi',
  FI: 'Suomi',
  EU: 'Europa',
  NL: 'Nederland'
};

export const FavoriteStoreContext = createContext();

export const parseStoredFavoriteStore = (raw, availableStores = []) => {
  if (!raw) return null;

  try {
    const parsed = JSON.parse(raw);
    if (parsed?.name) {
      return {
        name: parsed.name,
        id: parsed.id ?? null
      };
    }
  } catch {
    // Legacy plain-string value
  }

  if (typeof raw === 'string') {
    const match = availableStores.find(store => store.name === raw);
    return {
      name: raw,
      id: match?.id ?? null
    };
  }

  return null;
};

export const serializeFavoriteStore = store => {
  if (!store?.name) return null;
  return JSON.stringify({
    name: store.name,
    id: store.id ?? null
  });
};

const normalizeFavoriteStoreInput = (storeOrName, id, availableStores = []) => {
  if (!storeOrName) return null;

  if (typeof storeOrName === 'object' && storeOrName.name) {
    return {
      name: storeOrName.name,
      id: storeOrName.id ?? null
    };
  }

  if (typeof storeOrName === 'string') {
    const match = availableStores.find(store => store.name === storeOrName);
    return {
      name: storeOrName,
      id: id ?? match?.id ?? null
    };
  }

  return null;
};

const isStoredFavoriteStoreValid = (storedFavoriteStore, availableStores) => {
  const parsed = parseStoredFavoriteStore(storedFavoriteStore, availableStores);
  if (!parsed) return false;

  return availableStores.some(
    store => store.name === parsed.name || (parsed.id && store.id === parsed.id)
  );
};

export const useFavoriteStore = () => {
  const context = useContext(FavoriteStoreContext);
  if (!context) {
    throw new Error(
      'useFavoriteStore must be used within a FavoriteStoreProvider'
    );
  }
  return context;
};

const readStoredFavoriteStore = (availableStores = []) => {
  try {
    const stored = localStorage.getItem(FAVORITE_STORE_KEY);
    if (!stored) return null;
    return parseStoredFavoriteStore(stored, availableStores);
  } catch (e) {
    console.error('Error accessing localStorage:', e);
    return null;
  }
};

export const FavoriteStoreProvider = ({ children }) => {
  // ponytail: lazy localStorage seed so reload keeps the pick before effects run
  const [favoriteStore, setFavoriteStoreState] = useState(() =>
    typeof window === 'undefined' ? null : readStoredFavoriteStore()
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { loggedIn } = useAuth();
  const { selectedChannel } = useContext(ChannelContext);

  // Get customer data for Voyado API
  const { data: customerData, loading: customerLoading } = useQuery(
    customerIdQuery,
    {
      skip: !loggedIn,
      errorPolicy: 'ignore',
      fetchPolicy: 'no-cache'
    }
  );

  // Get stores data for matching
  const { data: storesData, loading: storesLoading } =
    useQuery(storeLocatorQuery);

  // Helper function to get stores for current region (same as PDP)
  const getAllStores = () => {
    const allStores = storesData?.stores || [];
    const currentRegion = countries[selectedChannel?.name];

    if (!currentRegion) {
      return allStores;
    }

    return allStores.filter(store => store.region === currentRegion);
  };

  const persistFavoriteStore = store => {
    setFavoriteStoreState(store);

    try {
      if (store?.name) {
        localStorage.setItem(FAVORITE_STORE_KEY, serializeFavoriteStore(store));
      } else {
        localStorage.removeItem(FAVORITE_STORE_KEY);
      }

      window.dispatchEvent(
        new CustomEvent('favoriteStoreChanged', {
          detail: store
        })
      );
    } catch (e) {
      console.error('Error saving to localStorage:', e);
    }
  };

  // Hydrate / validate localStorage for everyone (guests included).
  // Voyado is a logged-in fallback only — never wipe guest picks.
  useEffect(() => {
    const syncFavoriteStore = async () => {
      if (storesLoading) return;

      const availableStores = getAllStores();

      try {
        const storedFavoriteStore = localStorage.getItem(FAVORITE_STORE_KEY);
        if (storedFavoriteStore) {
          if (
            availableStores.length === 0 ||
            isStoredFavoriteStoreValid(storedFavoriteStore, availableStores)
          ) {
            setFavoriteStoreState(
              parseStoredFavoriteStore(storedFavoriteStore, availableStores)
            );
            return;
          }

          // Store not in this channel's list — drop it
          localStorage.removeItem(FAVORITE_STORE_KEY);
          setFavoriteStoreState(null);
        }
      } catch (e) {
        console.error('Error accessing localStorage:', e);
      }

      // Guests: localStorage is the source of truth
      if (
        !loggedIn ||
        !customerData?.customer?.externalId ||
        customerLoading
      ) {
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const url = `/api/customer_favorite_store?externalId=${encodeURIComponent(
          customerData.customer.externalId
        )}`;

        const response = await fetch(url);
        const data = await response.json();

        if (response.ok && data.favoriteStore) {
          const voyadoFavoriteStore = data.favoriteStore;
          let voyadoStoreName = null;

          if (typeof voyadoFavoriteStore === 'string') {
            voyadoStoreName = voyadoFavoriteStore;
          } else if (voyadoFavoriteStore.name) {
            voyadoStoreName = voyadoFavoriteStore.name;
          } else if (voyadoFavoriteStore.favoriteStore?.name) {
            voyadoStoreName = voyadoFavoriteStore.favoriteStore.name;
          }

          if (voyadoStoreName) {
            let matchingStore = availableStores.find(
              store => store.name === voyadoStoreName
            );

            if (!matchingStore) {
              matchingStore = availableStores.find(
                store =>
                  store.name
                    .toLowerCase()
                    .includes(voyadoStoreName.toLowerCase()) ||
                  voyadoStoreName
                    .toLowerCase()
                    .includes(store.name.toLowerCase())
              );
            }

            if (matchingStore) {
              persistFavoriteStore({
                name: matchingStore.name,
                id: matchingStore.id ?? null
              });
            }
          }
        } else {
          setError(data.error);
        }
      } catch (err) {
        console.error('Error fetching favorite store from Voyado:', err);
        setError('Failed to fetch favorite store');
      } finally {
        setLoading(false);
      }
    };

    syncFavoriteStore();
  }, [
    loggedIn,
    customerData?.customer?.externalId,
    customerLoading,
    storesLoading,
    selectedChannel?.name
  ]); // eslint-disable-line react-hooks/exhaustive-deps

  const setFavoriteStore = (storeOrName, id) => {
    const store = normalizeFavoriteStoreInput(
      storeOrName,
      id,
      getAllStores()
    );
    persistFavoriteStore(store);
  };

  const clearFavoriteStore = () => {
    setFavoriteStore(null);
  };

  const checkFavoriteStoreStock = product => {
    const favoriteStoreName = favoriteStore?.name;
    if (!favoriteStoreName || !product) {
      return null;
    }

    const hasVariants = product?.variants?.values?.length > 0;

    if (hasVariants) {
      const variants = product.variants.values;
      for (const variant of variants) {
        if (variant.warehouseStock?.length > 0) {
          const favoriteStoreStock = variant.warehouseStock.find(
            stock =>
              stock.location && stock.location.name === favoriteStoreName
          );

          if (
            favoriteStoreStock &&
            favoriteStoreStock.stockLevel > WAREHOUSE_STOCK_LEVEL_THRESHOLD
          ) {
            return true;
          }
        }
      }
      return false;
    }

    if (product.warehouseStock?.length > 0) {
      const favoriteStoreStock = product.warehouseStock.find(
        stock => stock.location && stock.location.name === favoriteStoreName
      );
      return favoriteStoreStock
        ? favoriteStoreStock.stockLevel > WAREHOUSE_STOCK_LEVEL_THRESHOLD
        : false;
    }
    return false;
  };

  return (
    <FavoriteStoreContext.Provider
      value={{
        favoriteStore,
        setFavoriteStore,
        clearFavoriteStore,
        checkFavoriteStoreStock,
        loading,
        error,
        getAllStores: () => getAllStores(),
        storesLoading
      }}
    >
      {children}
    </FavoriteStoreContext.Provider>
  );
};
